/**
 * Insights "explicit-tenant-only" policy — the ONE canonical definition of a
 * BROAD (wildcard) tenant-access grant and the "user holds insights_client"
 * read. Used to BLOCK creating new broad grants for insights_client users in
 * Manage tenant access (per-tenant grants only), and to auto-remove existing
 * ones.
 *
 * WHY (ROLE_ACCESS_BACKEND_NOTES §4.4): an insights_client's region zones are
 * set PER (user, tenant). A broad grant (All tenants / customer / sponsor) is a
 * wildcard that would hand them tenants whose zones were never configured — i.e.
 * unrestricted-by-default access to reports nobody scoped.
 *
 * Both inputs are now REAL, not mocked:
 *   - grants come from `accessGrantApi` (server-backed /api/admin/access-grants),
 *     whose `ScopeType` is the single source of what "scoped to one tenant"
 *     means — reused here rather than re-derived.
 *   - the permission read is RBAC v2 (`useCurrentUser().hasPermission`, or the
 *     `permissions` array of the user being edited); the old rolesModelApi mock
 *     is gone.
 *
 * This module does NOT read, write, or delete grants — it only defines the
 * policy and an audit breadcrumb. TODO(backend): enforce explicit-tenant-only on
 * the API too, so the client isn't the only boundary.
 */
import { INSIGHTS_CLIENT } from '../auth/useInsightsAccess';

import type { AccessGrant, ScopeType } from './accessGrantApi';

/** THE canonical "broad grant" test: anything NOT scoped to a single tenant
 *  (global / customer / sponsor) is a wildcard. No parallel definition. */
export const isBroadScope = (scope: ScopeType): boolean => scope !== 'tenant';

/** THE canonical "this user holds insights_client" read, over the real RBAC v2
 *  permission codes (`/api/me`'s `permissions`, or an edited user's set). */
export const roleHasInsightsClient = (perms: readonly string[] | undefined): boolean =>
  !!perms && perms.includes(INSIGHTS_CLIENT);

/** The grants an insights_client user must not keep — every non-tenant-scoped
 *  one. Pure, so the block-new and auto-remove sides share one rule. */
export const broadGrantsToRemove = (grants: readonly AccessGrant[]): AccessGrant[] =>
  grants.filter((g) => isBroadScope(g.scope_type));

/**
 * UI-side audit breadcrumb for the auto-removal of a broad grant from an
 * insights_client user. This is NOT the system of record: the real DELETE is a
 * server call and the authoritative audit must live server-side (TODO(backend)).
 * Kept intentionally minimal (console note) — no in-memory "flagged" limbo store.
 */
export function recordBroadGrantRemoval(
  userId: number,
  grant: { id: number; scope_type: ScopeType; scope_code: string },
): void {
  // eslint-disable-next-line no-console
  console.info('[insights-policy] auto-removed broad grant (explicit-tenant-only)', {
    userId,
    grantId: grant.id,
    scope: grant.scope_type,
    code: grant.scope_code || '(all)',
  });
}
